import React from 'react';
import { HighlightWithPlayer } from '../../types/highlights.types';
import { FaPlay, FaDownload, FaShareAlt } from 'react-icons/fa';
import '../../styles/components/highlights.scss';

interface FeaturedHighlightProps {
  highlight: HighlightWithPlayer;
  onPlay: (highlight: HighlightWithPlayer) => void;
  onDownload: (highlight: HighlightWithPlayer) => void;
  onShare: (highlight: HighlightWithPlayer) => void;
}

const FeaturedHighlight: React.FC<FeaturedHighlightProps> = ({
  highlight,
  onPlay,
  onDownload,
  onShare,
}) => {
  const formatDuration = (seconds: number): string => {
    return `${seconds.toFixed(1)}s`;
  };

  const getTagDisplay = (tag: string): string => {
    const tagMap: { [key: string]: string } = {
      'OneK': '1K',
      'TwoK': '2K',
      'ThreeK': '3K',
      'FourK': '4K',
      'Ace': 'ACE',
    };
    return tagMap[tag] || tag;
  };

  const getPrimaryTag = (): string => {
    const priorityTags = ['Ace', 'FourK', 'ThreeK', 'TwoK', 'OneK'];
    for (const tag of priorityTags) {
      if (highlight.tags.includes(tag)) {
        return getTagDisplay(tag);
      }
    }
    const clutchTag = highlight.tags.find(t => t.includes('1v'));
    if (clutchTag) return clutchTag;
    return `${highlight.killCount}K`;
  };

  const getHighlightTitle = (): string => {
    const primaryTag = getPrimaryTag();
    return primaryTag;
  };

  const getPlayerAvatar = (): string => {
    if (highlight.playerAvatar) {
      return highlight.playerAvatar;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(highlight.playerName || 'Player')}&background=4294d0&color=fff&size=240`;
  };

  return (
    <div className="featured-highlight">
      <div className="featured-highlight__video" onClick={() => onPlay(highlight)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPlay(highlight); }}>
        <video
          className="featured-highlight__video-preview"
          src={highlight.clipUrl}
          preload="metadata"
        />
        <div className="featured-highlight__play-overlay">
          <FaPlay className="featured-highlight__play-icon" />
        </div>
        <div className="featured-highlight__duration">
          {formatDuration(highlight.clipLengthSeconds)}
        </div>
      </div>

      <div className="featured-highlight__info">
        <div className="featured-highlight__player">
          <div className="featured-highlight__avatar">
            <img src={getPlayerAvatar()} alt={highlight.playerName || 'Player'} className="avatar-img" />
          </div>
          <div className="featured-highlight__player-details">
            <p className="featured-highlight__player-name">
              {highlight.playerName || 'Unknown Player'}
            </p>
            <div className="featured-highlight__meta">
              <span className="featured-highlight__round">Round {highlight.roundNumber}</span>
              <span className="featured-highlight__separator">|</span>
              <span className="featured-highlight__tag">{getHighlightTitle()}</span>
            </div>
          </div>
        </div>

        <div className="featured-highlight__actions">
          <button
            className="featured-highlight__action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(highlight);
            }}
            title="Download clip"
          >
            <FaDownload />
          </button>
          <button
            className="featured-highlight__action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onShare(highlight);
            }}
            title="Share clip"
          >
            <FaShareAlt />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeaturedHighlight;
